/* Basemap catalogue and the ORBIS dark tactical style.
 *
 * ── Two modes, and the distinction matters ─────────────────────────────────
 *
 *  'live'   — fetches tiles from upstream. DEVELOPMENT ONLY. Convenient, current,
 *             and a direct violation of CON-01 if it ever shipped.
 *  'bundle' — reads a staged PMTiles/COG bundle from Atlas. The production path.
 *             Offline, digest-pinned, reproducible (AD-05).
 *
 * The UI labels the active mode so nobody can mistake a live-tile session for an
 * air-gapped one. Production builds set VITE_MAP_MODE=bundle and the live sources
 * are unreachable by construction.
 *
 * All upstream sources below are free, keyless, and permissively licensed —
 * see docs/06-data-sources/GEOSPATIAL-DATA-SOURCES.md.
 */
import type { StyleSpecification, LayerSpecification, SkySpecification } from 'maplibre-gl'
import { bathymetry, graticule, land } from '../data/geo'
import { availableBasemaps, bundleManifest, findLayer, layerForBasemap, tileUrl } from './bundle'

export type MapMode = 'live' | 'bundle'
export type BasemapId = 'tactical' | 'satellite' | 'terrain-light' | 'offline'

export const MAP_MODE: MapMode =
  (import.meta.env.VITE_MAP_MODE as MapMode | undefined) ?? 'live'

// ── Upstream endpoints ───────────────────────────────────────────────────────

const OFM = 'https://tiles.openfreemap.org'
const EOX = 'https://tiles.maps.eox.at/wmts/1.0.0'
const AWS_DEM = 'https://s3.amazonaws.com/elevation-tiles-prod'

export const ATTRIBUTION = {
  tactical: '© OpenStreetMap contributors · OpenFreeMap (ODbL)',
  satellite: 'Sentinel-2 cloudless © EOX IT Services (CC BY 4.0) · Copernicus',
  'terrain-light': 'Terrain Light © EOX IT Services (CC BY 4.0)',
  offline: 'Placeholder geometry — staged Atlas bundle in production',
  terrain: 'DEM: Mapzen/AWS Terrain Tiles · SRTM, GMTED, ETOPO1 (public domain)',
} as const

export interface BasemapDef {
  id: BasemapId
  label: string
  kind: 'vector' | 'raster' | 'synthetic'
  /** Whether this basemap needs upstream network access. */
  live: boolean
  note?: string
}

export const BASEMAPS: BasemapDef[] = [
  { id: 'tactical', label: 'Tactical (vector)', kind: 'vector', live: true, note: 'OSM · dark' },
  { id: 'satellite', label: 'Satellite', kind: 'raster', live: true, note: 'Sentinel-2 cloudless' },
  { id: 'terrain-light', label: 'Terrain', kind: 'raster', live: true, note: 'EOX terrain light' },
  { id: 'offline', label: 'Offline (no tiles)', kind: 'synthetic', live: false, note: 'CON-01 demo' },
]

// ── Palette: the map must RECEDE behind symbology ────────────────────────────
// Every value here is deliberately low-contrast. On a tactical display the
// basemap is context, not content — if it competes with the symbols it is wrong.

const C = {
  sea: '#070d18',
  seaDeep: '#050a13',
  land: '#101827',
  landAlt: '#131c2d',
  green: '#122031',
  ice: '#1b2536',
  boundaryIntl: 'rgba(150,175,205,0.55)',
  boundaryRegion: 'rgba(130,155,185,0.22)',
  coast: 'rgba(120,150,185,0.42)',
  roadMajor: 'rgba(120,140,170,0.30)',
  roadMinor: 'rgba(110,130,160,0.16)',
  rail: 'rgba(120,140,170,0.22)',
  runway: 'rgba(150,175,205,0.40)',
  building: 'rgba(140,160,190,0.10)',
  labelCity: '#c4d2e4',
  labelWater: '#5f7fa5',
  labelHalo: 'rgba(5,10,20,0.92)',
  gratMinor: 'rgba(255,255,255,0.040)',
  gratMajor: 'rgba(255,255,255,0.085)',
  bathy: '#16304f',
}

const FONT = ['Noto Sans Regular']
const FONT_BOLD = ['Noto Sans Bold']

// ── Shared overlay layers (graticule + bathymetry) ───────────────────────────

function overlaySources() {
  return {
    orbisGraticule: { type: 'geojson' as const, data: graticule },
    orbisBathy: { type: 'geojson' as const, data: bathymetry },
  }
}

function overlayLayers(): LayerSpecification[] {
  return [
    {
      id: 'orbis-bathy',
      type: 'line',
      source: 'orbisBathy',
      paint: { 'line-color': C.bathy, 'line-width': 1, 'line-dasharray': [3, 3] },
    },
    {
      id: 'orbis-graticule-minor',
      type: 'line',
      source: 'orbisGraticule',
      filter: ['!', ['get', 'major']],
      paint: { 'line-color': C.gratMinor, 'line-width': 0.6 },
    },
    {
      id: 'orbis-graticule-major',
      type: 'line',
      source: 'orbisGraticule',
      filter: ['get', 'major'],
      paint: { 'line-color': C.gratMajor, 'line-width': 0.9 },
    },
  ]
}

/** raster-dem source for hillshade + 3D terrain. Terrarium encoding, public domain. */
function demSource() {
  return {
    orbisDem: {
      type: 'raster-dem' as const,
      tiles: [`${AWS_DEM}/terrarium/{z}/{x}/{y}.png`],
      encoding: 'terrarium' as const,
      tileSize: 256,
      maxzoom: 13,
      attribution: ATTRIBUTION.terrain,
    },
  }
}

/** Space + atmosphere. Without it the globe's limb is invisible against the page. */
function sky(): SkySpecification {
  return {
    'sky-color': '#0a1020',
    'horizon-color': '#2b4a72',
    'fog-color': '#0a1020',
    'fog-ground-blend': 0.6,
    'horizon-fog-blend': 0.35,
    'sky-horizon-blend': 0.6,
    // Atmosphere fades out as you descend — at tactical zoom it would just wash
    // the picture out.
    'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 4, 0.6, 7, 0],
  }
}

function hillshadeLayer(): LayerSpecification {
  return {
    id: 'orbis-hillshade',
    type: 'hillshade',
    source: 'orbisDem',
    paint: {
      'hillshade-exaggeration': 0.28,
      'hillshade-shadow-color': '#000913',
      'hillshade-highlight-color': '#2a3a52',
      'hillshade-accent-color': '#0d1522',
    },
  }
}

// ── 1. Tactical vector style — the primary basemap ───────────────────────────

function tacticalStyle(globe: boolean): StyleSpecification {
  return {
    version: 8,
    projection: { type: globe ? 'globe' : 'mercator' },
    sky: globe ? sky() : undefined,
    glyphs: `${OFM}/fonts/{fontstack}/{range}.pbf`,
    sources: {
      openmaptiles: { type: 'vector', url: `${OFM}/planet` },
      ...demSource(),
      ...overlaySources(),
    },
    layers: [
      // OpenMapTiles has NO land polygon: the background IS the landmass and water
      // is drawn over it. Painting the background as sea leaves land nearly invisible.
      { id: 'background', type: 'background', paint: { 'background-color': C.land } },

      // Landcover is only forest/ice/sand etc — texture on top of land, not the land.
      {
        id: 'landcover',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landcover',
        paint: { 'fill-color': C.green, 'fill-opacity': 0.35 },
      },
      {
        id: 'landuse',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landuse',
        paint: { 'fill-color': C.landAlt, 'fill-opacity': 0.30 },
      },
      {
        id: 'park',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'park',
        paint: { 'fill-color': C.green, 'fill-opacity': 0.45 },
      },

      hillshadeLayer(),

      // Water — drawn over land so coastlines read correctly
      {
        id: 'water',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'water',
        filter: ['!=', ['get', 'brunnel'], 'tunnel'],
        paint: { 'fill-color': C.seaDeep },
      },
      {
        id: 'water-outline',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'water',
        minzoom: 3,
        paint: { 'line-color': C.coast, 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.4, 10, 1.1] },
      },
      {
        id: 'waterway',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'waterway',
        minzoom: 7,
        paint: { 'line-color': C.seaDeep, 'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.5, 14, 2] },
      },

      // Buildings — barely there, only close in
      {
        id: 'building',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'building',
        minzoom: 13,
        paint: { 'fill-color': C.building },
      },

      // Transportation — ground mobility, not decoration
      {
        id: 'road-minor',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 10,
        filter: ['in', ['get', 'class'], ['literal', ['minor', 'service', 'track']]],
        paint: { 'line-color': C.roadMinor, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 16, 2] },
      },
      {
        id: 'road-major',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 6,
        filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary', 'secondary']]],
        paint: { 'line-color': C.roadMajor, 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 12, 2, 16, 5] },
      },
      {
        id: 'rail',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 8,
        filter: ['==', ['get', 'class'], 'rail'],
        paint: { 'line-color': C.rail, 'line-width': 0.7, 'line-dasharray': [4, 2] },
      },
      // Runways matter: they gate which aircraft can operate where
      {
        id: 'aeroway',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'aeroway',
        minzoom: 9,
        paint: { 'line-color': C.runway, 'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.8, 14, 4] },
      },

      // Boundaries — prominent. On a military map these are the point.
      {
        id: 'boundary-region',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['all', ['>=', ['get', 'admin_level'], 3], ['<=', ['get', 'admin_level'], 4]],
        minzoom: 4,
        paint: { 'line-color': C.boundaryRegion, 'line-width': 0.7, 'line-dasharray': [3, 2] },
      },
      {
        id: 'boundary-country',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['<=', ['get', 'admin_level'], 2],
        paint: {
          'line-color': C.boundaryIntl,
          'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.6, 6, 1.2, 12, 2.2],
          'line-dasharray': [6, 3],
        },
      },

      ...overlayLayers(),

      // Labels — restrained. Symbology owns the visual hierarchy.
      {
        id: 'label-water',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'water_name',
        minzoom: 4,
        layout: {
          // Prefer the English exonym: OSM `name` on the Baltic is a five-language
          // stack, which is unreadable on a tactical display.
          'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
          'text-font': FONT,
          'text-size': ['interpolate', ['linear'], ['zoom'], 4, 9, 10, 12],
          'text-letter-spacing': 0.14,
          'text-transform': 'uppercase',
          'text-max-width': 9,
        },
        paint: { 'text-color': C.labelWater, 'text-halo-color': C.labelHalo, 'text-halo-width': 1.2 },
      },
      {
        id: 'label-place',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['country', 'state', 'city', 'town']]],
        layout: {
          'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
          // `text-font` is array<string>: a `case` must yield ['literal', [...]],
          // not a bare string.
          'text-font': [
            'case',
            ['==', ['get', 'class'], 'country'],
            ['literal', FONT_BOLD],
            ['literal', FONT],
          ],
          // MapLibre permits only ONE zoom-based interpolate per expression, so the
          // zoom interpolate is top-level and the per-class variation lives inside
          // each stop — not the other way round.
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            2, ['match', ['get', 'class'], 'country', 10, 'state', 9, 'city', 9, 8],
            6, ['match', ['get', 'class'], 'country', 14, 'state', 11, 'city', 11, 9],
            12, ['match', ['get', 'class'], 'country', 15, 'state', 12, 'city', 14, 10],
          ],
          'text-letter-spacing': ['match', ['get', 'class'], 'country', 0.2, 0.05],
          'text-transform': ['match', ['get', 'class'], 'country', 'uppercase', 'none'],
          'text-max-width': 8,
        },
        paint: {
          'text-color': C.labelCity,
          'text-halo-color': C.labelHalo,
          'text-halo-width': 1.4,
          'text-opacity': ['match', ['get', 'class'], 'country', 0.85, 0.7],
        },
      },
      {
        id: 'label-aerodrome',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'aerodrome_label',
        minzoom: 8,
        layout: {
          'text-field': ['coalesce', ['get', 'iata'], ['get', 'name']],
          'text-font': FONT,
          'text-size': 10,
          'text-letter-spacing': 0.08,
        },
        paint: { 'text-color': C.runway, 'text-halo-color': C.labelHalo, 'text-halo-width': 1.2 },
      },
    ],
  }
}

// ── 2 & 3. Raster basemaps (EOX WMTS) ────────────────────────────────────────

function rasterStyle(
  layer: 's2cloudless-2020' | 'terrain-light',
  attribution: string,
  globe: boolean,
): StyleSpecification {
  return {
    version: 8,
    projection: { type: globe ? 'globe' : 'mercator' },
    sky: globe ? sky() : undefined,
    glyphs: `${OFM}/fonts/{fontstack}/{range}.pbf`,
    sources: {
      base: {
        type: 'raster',
        tiles: [`${EOX}/${layer}_3857/default/g/{z}/{y}/{x}.jpg`],
        tileSize: 256,
        maxzoom: layer === 's2cloudless-2020' ? 14 : 16,
        attribution,
      },
      // Country outlines over imagery — without them satellite is unreadable tactically
      openmaptiles: { type: 'vector', url: `${OFM}/planet` },
      ...demSource(),
      ...overlaySources(),
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': C.sea } },
      {
        id: 'base',
        type: 'raster',
        source: 'base',
        // Desaturate and darken so symbology stays dominant over imagery
        paint: { 'raster-saturation': -0.35, 'raster-brightness-max': 0.72, 'raster-contrast': 0.06 },
      },
      {
        id: 'boundary-country',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['<=', ['get', 'admin_level'], 2],
        paint: {
          'line-color': 'rgba(190,210,235,0.6)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.6, 6, 1.2, 12, 2.2],
          'line-dasharray': [6, 3],
        },
      },
      ...overlayLayers(),
      {
        id: 'label-place',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['country', 'city']]],
        layout: {
          'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
          'text-font': FONT,
          'text-size': 11,
          'text-letter-spacing': 0.1,
          'text-max-width': 8,
        },
        paint: { 'text-color': '#dbe6f2', 'text-halo-color': C.labelHalo, 'text-halo-width': 1.5 },
      },
    ],
  }
}

// ── 4. Offline — inline geometry only, proves CON-01 ─────────────────────────

/* ── Bundle mode: the same map, from staged tiles ──────────────────────────
 *
 * Every source below is same-origin. There is no upstream to reach, which is what makes this the
 * production posture rather than a stricter setting of the development one.
 *
 * When no bundle is staged this falls through to `offlineStyle` — inline coastline geometry, no
 * tiles at all. That is a working map of last resort and the badge says what it is. What it never
 * does is fall back to the network.
 */
function bundleStyle(id: BasemapId, globe: boolean): StyleSpecification {
  const manifest = bundleManifest()
  // The layer for THIS basemap, not merely the first raster in the bundle. Rendering satellite
  // imagery under the label "Terrain" would be a map lying about what it is showing.
  const raster = layerForBasemap(manifest, id)
  if (!raster) return offlineStyle(globe)

  const dem = findLayer(manifest, 'dem')
  return {
    version: 8,
    projection: { type: globe ? 'globe' : 'mercator' },
    sky: globe ? sky() : undefined,
    // No `glyphs`: the upstream font endpoint is a network call, and a staged bundle carries no
    // glyph pack yet. Labels come from the symbology layer, which uses the Deck's own bundled
    // fonts — so the map is unlabelled and the symbols are not, which is the correct priority on
    // a tactical display.
    sources: {
      base: {
        type: 'raster',
        tiles: [tileUrl(raster)],
        tileSize: 256,
        minzoom: raster.zoomMin ?? 0,
        maxzoom: raster.zoomMax ?? 10,
        attribution: raster.attribution,
      },
      ...(dem
        ? {
            orbisDem: {
              type: 'raster-dem' as const,
              tiles: [tileUrl(dem)],
              encoding: 'terrarium' as const,
              tileSize: 256,
              maxzoom: dem.zoomMax ?? 10,
              attribution: dem.attribution,
            },
          }
        : {}),
      ...overlaySources(),
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': C.sea } },
      {
        id: 'base',
        type: 'raster',
        source: 'base',
        // Same treatment as live imagery: the basemap is context, not content.
        paint: { 'raster-saturation': -0.35, 'raster-brightness-max': 0.72, 'raster-contrast': 0.06 },
      },
      ...overlayLayers(),
    ],
  }
}

function offlineStyle(globe: boolean): StyleSpecification {
  return {
    version: 8,
    projection: { type: globe ? 'globe' : 'mercator' },
    sky: globe ? sky() : undefined,
    sources: { orbisLand: { type: 'geojson', data: land }, ...overlaySources() },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': C.sea } },
      { id: 'land-fill', type: 'fill', source: 'orbisLand', paint: { 'fill-color': C.land } },
      {
        id: 'land-line',
        type: 'line',
        source: 'orbisLand',
        paint: { 'line-color': C.coast, 'line-width': 1.1 },
      },
      ...overlayLayers(),
    ],
  }
}

// ── Public ───────────────────────────────────────────────────────────────────

/**
 * `globe` is baked into the style rather than applied afterwards with
 * `map.setProjection`. Projection and sky are STYLE properties: calling setStyle for
 * a basemap change silently reset both back to mercator, so the 3D toggle appeared
 * to do nothing after any basemap switch. Declaring them here makes that impossible.
 */
export function buildStyle(id: BasemapId, globe = false): StyleSpecification {
  // In bundle mode the basemap id selects nothing: a bundle has the layers it was staged with,
  // and offering a chooser over layers that may not exist would be a menu of things that turn the
  // map grey. The menu already marks live basemaps unavailable in this mode.
  if (MAP_MODE === 'bundle') return id === 'offline' ? offlineStyle(globe) : bundleStyle(id, globe)
  switch (id) {
    case 'tactical':
      return tacticalStyle(globe)
    case 'satellite':
      return rasterStyle('s2cloudless-2020', ATTRIBUTION.satellite, globe)
    case 'terrain-light':
      return rasterStyle('terrain-light', ATTRIBUTION['terrain-light'], globe)
    case 'offline':
      return offlineStyle(globe)
  }
}

/** Whether a basemap can be drawn at all on this deployment.
 *
 *  In live mode every basemap works. In bundle mode only the ones the bundle staged do — and the
 *  menu greys out the rest rather than offering a choice that turns the map grey. */
export function basemapAvailable(id: BasemapId): boolean {
  if (MAP_MODE !== 'bundle') return true
  return id === 'offline' || availableBasemaps(bundleManifest()).includes(id)
}

/** Does this style carry a raster-dem source that terrain/hillshade can use? */
export function styleHasDem(id: BasemapId): boolean {
  if (MAP_MODE === 'bundle') {
    return !!layerForBasemap(bundleManifest(), id) && !!findLayer(bundleManifest(), 'dem')
  }
  return MAP_MODE === 'live' && id !== 'offline'
}
